/**
 * Direction (up/down) color schemes — the single, reusable source of truth for
 * the user-configurable price-direction palette.
 *
 * Green=up / red=down is a Western convention; China/Taiwan/Hong Kong/Japan/Korea
 * invert it (red=up/gain, green=down/loss), and some users need colorblind-safe
 * or high-contrast palettes. This module defines the presets and writes the
 * chosen colors to CSS variables (`--color-up` / `--color-down`) so every DOM
 * surface that uses `COLORS.up` / `COLORS.down` (= `rgb(var(--color-up) / α)`)
 * re-themes live, with no re-render.
 *
 * Pure/framework-agnostic and DOM-guarded so it can be shared verbatim across the
 * perps app, the host app, and the standalone frontend.
 */

export type DirectionSchemeName =
  | "classic"
  | "eastAsian"
  | "colorblind"
  | "mono"
  | "custom";

/** Up/down colors as CSS RGB triplets ("R G B") so `rgb(<triplet> / α)` works. */
export interface DirectionColors {
  up: string;
  down: string;
}

/** Built-in presets. Values match the `--color-up`/`--color-down` fallbacks. */
export const DIRECTION_PRESETS: Record<
  Exclude<DirectionSchemeName, "custom">,
  DirectionColors
> = {
  classic: { up: "22 199 132", down: "234 57 67" }, // green up / red down
  eastAsian: { up: "234 57 67", down: "22 199 132" }, // red up / green down
  colorblind: { up: "47 128 237", down: "242 153 74" }, // blue up / orange down
  mono: { up: "237 237 237", down: "122 122 122" }, // light up / gray down
};

export const DEFAULT_DIRECTION_SCHEME: DirectionSchemeName = "classic";

/** East-Asian markets that conventionally use red=up / green=down. */
const EAST_ASIAN_COUNTRY_MATCHERS = [
  "china",
  "taiwan",
  "hong kong",
  "japan",
  "korea", // covers "South Korea" / "Korea"
];

/**
 * Map an IP-geolocated country name (from the geoblock endpoint's `country`
 * field, e.g. "United States of America") to a default scheme. Only used to seed
 * the FIRST-load default — once the user picks a scheme it is never consulted.
 */
export const countryToScheme = (country?: string): DirectionSchemeName => {
  if (!country) return DEFAULT_DIRECTION_SCHEME;
  const c = country.trim().toLowerCase();
  return EAST_ASIAN_COUNTRY_MATCHERS.some((m) => c.includes(m))
    ? "eastAsian"
    : "classic";
};

/** Resolve the {up,down} triplets for a scheme (custom falls back to classic). */
export const resolveDirectionColors = (
  scheme: DirectionSchemeName,
  custom?: DirectionColors
): DirectionColors => {
  if (scheme === "custom") return custom ?? DIRECTION_PRESETS.classic;
  return DIRECTION_PRESETS[scheme] ?? DIRECTION_PRESETS.classic;
};

/** WCAG relative luminance of an "R G B" triplet (0 = black, 1 = white). */
const relativeLuminance = (triplet: string): number => {
  const [r, g, b] = triplet
    .trim()
    .split(/\s+/)
    .map((x) => parseInt(x, 10) / 255);
  const lin = (c: number) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

/**
 * The readable text/icon color ("R G B" triplet) to place ON a solid fill of the
 * given direction color — near-black or white, whichever has higher WCAG
 * contrast. This is what makes buttons like Buy/Long and Sell/Short legible
 * regardless of the chosen scheme: bright fills (classic green, colorblind
 * orange) get dark text, dark fills (classic red) get white. Computed here so it
 * tracks `--color-up`/`--color-down` — a fixed constant would fail on any preset
 * whose brightness differs from `classic` (e.g. `colorblind`'s orange down).
 */
export const onColorTriplet = (triplet: string): string => {
  const L = relativeLuminance(triplet);
  const contrastWhite = (1 + 0.05) / (L + 0.05);
  // Trading convention: action buttons carry WHITE text (Buy/Sell, Place Order).
  // Only switch to near-black when white would be unreadable — these buttons are
  // large + bold, so WCAG AA-large (3:1) is the bar. This keeps the conventional
  // white on classic red (~4.1:1) and colorblind blue (~3.9:1), but gives dark
  // text on bright fills where white collapses: classic green (~2.2:1) and
  // colorblind orange (~2.3:1). Matches the approved "dark on green / white on
  // red" rule while staying correct for every scheme + custom colors.
  return contrastWhite >= 3 ? "255 255 255" : "6 6 12";
};

/**
 * Write `--color-up` / `--color-down` (RGB triplets) onto the document root so
 * `rgb(var(--color-up) / α)` and `cssColor("--color-up", …)` resolve to the
 * selected scheme. Also writes `--color-up-on` / `--color-down-on` — the
 * on-fill text color — so `COLORS.up.on` / `COLORS.down.on` stay legible on
 * every scheme. No-op outside the browser (SSR-safe).
 */
export const applyDirectionColors = (
  scheme: DirectionSchemeName,
  custom?: DirectionColors
): void => {
  if (typeof document === "undefined") return;
  const { up, down } = resolveDirectionColors(scheme, custom);
  const root = document.documentElement;
  root.style.setProperty("--color-up", up);
  root.style.setProperty("--color-down", down);
  root.style.setProperty("--color-up-on", onColorTriplet(up));
  root.style.setProperty("--color-down-on", onColorTriplet(down));
};

/**
 * Normalize a color the color-picker produces (hex "#16C784", "r,g,b", or an
 * "R G B" triplet) into the CSS RGB triplet form the vars expect.
 */
export const toRgbTriplet = (color: string): string => {
  const s = (color || "").trim();
  const hex = s.match(/^#?([0-9a-fA-F]{6})$/);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
  }
  const parts = s.split(/[\s,]+/).filter(Boolean);
  return parts.length === 3 ? parts.join(" ") : s;
};

/** Convert an "R G B" triplet to a "#rrggbb" hex string (for color inputs). */
export const tripletToHex = (triplet: string): string => {
  const parts = (triplet || "").trim().split(/\s+/).map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return "#000000";
  const h = (x: number) => Math.max(0, Math.min(255, x)).toString(16).padStart(2, "0");
  return `#${h(parts[0])}${h(parts[1])}${h(parts[2])}`;
};
